// JSON template registry — auto-loads every *.json file in this
// directory via Vite's import.meta.glob and exposes them as a map
// keyed by template id.
//
// Adding a new template = drop a `<id>.json` file alongside this
// registry. Vite picks it up on next build (or on HMR in dev). No
// other code changes needed.
//
// The registry is the single source of truth for "what JSON templates
// exist." StepSocialDesign's picker reads from here; StepSocialFinal's
// dispatcher reads from here.

import type { JsonTemplate } from './types';

// Vite eagerly imports every .json in this directory at build time.
// `as: 'json'` parses each as JSON so we get typed objects, not strings.
const modules = import.meta.glob<JsonTemplate>('./*.json', {
  eager: true,
  import: 'default',
});

/** Map of template id → parsed JsonTemplate config. */
export const JSON_TEMPLATES: Record<string, JsonTemplate> = (() => {
  const out: Record<string, JsonTemplate> = {};
  for (const path in modules) {
    const tpl = modules[path];
    if (!tpl || typeof tpl !== 'object' || !tpl.id) {
      console.warn(`[json-templates] Skipping ${path}: missing or invalid id field`);
      continue;
    }
    if (out[tpl.id]) {
      console.warn(`[json-templates] Duplicate id "${tpl.id}" in ${path}`);
    }
    out[tpl.id] = tpl;
  }
  return out;
})();

/** Array of registered template ids — used by the picker + dispatcher. */
export function getJsonTemplateIds(): string[] {
  return Object.keys(JSON_TEMPLATES);
}

/** Lookup by id. Returns undefined if no JSON template with that id. */
export function getJsonTemplate(id: string): JsonTemplate | undefined {
  return JSON_TEMPLATES[id];
}

/** True if the given id refers to a JSON template (vs. a legacy
 *  hand-coded React template). The dispatcher uses this to decide
 *  between <JsonSocialPost> and PostXXX components. */
export function isJsonTemplate(id: string): boolean {
  return id in JSON_TEMPLATES;
}

/** Look up the slotColor for a given template id (used by frontend
 *  when sending `bg_hex` to the backend's social-bg-* compose endpoint).
 *  Returns null if the id isn't a JSON template — caller falls back
 *  to the legacy SOCIAL_DESIGN_BG_COLOR map. */
export function getJsonTemplateSlotColor(id: string): string | null {
  return JSON_TEMPLATES[id]?.slotColor ?? null;
}
